import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { externalAccountsAPI, externalTransfersAPI, accountsAPI } from '../lib/api';
import { formatCurrency } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { ExternalLink, Plus, Trash2, Building2, ArrowUpRight, Send, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import DollarInput from '../components/DollarInput';
import PreTransferConfirm from '../components/PreTransferConfirm';
import TransferConfirmation from '../components/TransferConfirmation';

export default function ExternalAccountsPage() {
  const [externalAccounts, setExternalAccounts] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [transferDialogOpen, setTransferDialogOpen] = useState(false);
  const [selectedExtAccount, setSelectedExtAccount] = useState(null);
  const navigate = useNavigate();

  // Add external account form
  const [bankName, setBankName] = useState('');
  const [accountHolderName, setAccountHolderName] = useState('');
  const [accountNumber, setAccountNumber] = useState('');
  const [routingNumber, setRoutingNumber] = useState('');
  const [accountType, setAccountType] = useState('checking');
  const [nickname, setNickname] = useState('');
  const [addLoading, setAddLoading] = useState(false);

  // Transfer form
  const [fromAccountId, setFromAccountId] = useState('');
  const [transferAmount, setTransferAmount] = useState('');
  const [transferMemo, setTransferMemo] = useState('');
  const [transferLoading, setTransferLoading] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmDetails, setConfirmDetails] = useState(null);
  const [preConfirmOpen, setPreConfirmOpen] = useState(false);
  const [preConfirmDetails, setPreConfirmDetails] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [extRes, accRes] = await Promise.all([
        externalAccountsAPI.list(),
        accountsAPI.list()
      ]);
      setExternalAccounts(extRes.data.external_accounts);
      setAccounts(accRes.data.accounts);
      if (accRes.data.accounts.length > 0) {
        setFromAccountId(accRes.data.accounts[0]._id);
      }
    } catch (err) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleAddAccount = async (e) => {
    e.preventDefault();
    if (!bankName || !accountNumber || !routingNumber || !accountHolderName) {
      toast.error('Please fill in all required fields');
      return;
    }
    setAddLoading(true);
    try {
      await externalAccountsAPI.add({
        bank_name: bankName,
        account_holder_name: accountHolderName,
        account_number: accountNumber,
        routing_number: routingNumber,
        account_type: accountType,
        nickname
      });
      toast.success('External account added successfully!');
      setBankName(''); setAccountHolderName(''); setAccountNumber('');
      setRoutingNumber(''); setNickname(''); setAccountType('checking');
      setAddDialogOpen(false);
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add account');
    } finally {
      setAddLoading(false);
    }
  };

  const handleRemoveAccount = async (id) => {
    try {
      await externalAccountsAPI.remove(id);
      toast.success('External account removed');
      loadData();
    } catch (err) {
      toast.error('Failed to remove account');
    }
  };

  const openTransferDialog = (extAcc) => {
    setSelectedExtAccount(extAcc);
    setTransferAmount('');
    setTransferMemo('');
    setTransferDialogOpen(true);
  };

  const handleExternalTransfer = async (e) => {
    e.preventDefault();
    if (!transferAmount || parseFloat(transferAmount) <= 0) { toast.error('Enter a valid amount'); return; }
    setPreConfirmDetails({
      type: 'External Transfer',
      amount: parseFloat(transferAmount),
      fromAccount: selectedFromAccount?.name || 'Account',
      recipient: selectedExtAccount?.nickname || selectedExtAccount?.bank_name,
    });
    setPreConfirmOpen(true);
  };

  const executeExternalTransfer = async () => {
    setTransferLoading(true);
    try {
      await externalTransfersAPI.send({
        from_account_id: fromAccountId,
        external_account_id: selectedExtAccount._id,
        amount: parseFloat(transferAmount),
        memo: transferMemo
      });
      setPreConfirmOpen(false);
      setConfirmDetails({
        type: 'External Transfer',
        amount: parseFloat(transferAmount),
        recipient: selectedExtAccount.nickname || selectedExtAccount.bank_name,
        fromAccount: selectedFromAccount?.name || 'Account',
      });
      setTransferDialogOpen(false);
      setConfirmOpen(true);
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Transfer failed');
    } finally { setTransferLoading(false); }
  };

  const selectedFromAccount = accounts.find(a => a._id === fromAccountId);

  if (loading) {
    return <div className="page-container"><p>Loading external accounts...</p></div>;
  }

  return (
    <div className="page-container">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-semibold tracking-tight" style={{ fontFamily: 'Space Grotesk' }}>External Accounts</h2>
          <p className="text-sm text-muted-foreground mt-1">Manage your linked external bank accounts and send transfers</p>
        </div>
        <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-external-account-button">
              <Plus className="h-4 w-4 mr-2" /> Add External Account
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Space Grotesk' }}>Add External Bank Account</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleAddAccount} className="space-y-4">
              <div className="space-y-2">
                <Label>Bank Name</Label>
                <Input data-testid="ext-bank-name-input" placeholder="Chase, Wells Fargo, Bank of America..." value={bankName} onChange={e => setBankName(e.target.value)} required />
              </div>
              <div className="space-y-2">
                <Label>Account Holder Name</Label>
                <Input data-testid="ext-holder-name-input" placeholder="Full legal name" value={accountHolderName} onChange={e => setAccountHolderName(e.target.value)} required />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Account Number</Label>
                  <Input data-testid="ext-account-number-input" placeholder="123456789" value={accountNumber} onChange={e => setAccountNumber(e.target.value)} required />
                </div>
                <div className="space-y-2">
                  <Label>Routing Number</Label>
                  <Input data-testid="ext-routing-number-input" placeholder="021000021" value={routingNumber} onChange={e => setRoutingNumber(e.target.value)} required />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Account Type</Label>
                  <Select value={accountType} onValueChange={setAccountType}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="checking">Checking</SelectItem>
                      <SelectItem value="savings">Savings</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Nickname (Optional)</Label>
                  <Input placeholder="My Chase Account" value={nickname} onChange={e => setNickname(e.target.value)} />
                </div>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setAddDialogOpen(false)}>Cancel</Button>
                <Button data-testid="ext-add-submit-button" type="submit" disabled={addLoading}>
                  {addLoading ? 'Adding...' : 'Add Account'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* External Accounts List */}
      {externalAccounts.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <ExternalLink className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
            <p className="text-lg font-medium">No External Accounts</p>
            <p className="text-sm text-muted-foreground mt-1">Add an external bank account to start sending transfers</p>
            <Button className="mt-4" onClick={() => setAddDialogOpen(true)}>
              <Plus className="h-4 w-4 mr-2" /> Add Your First Account
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {externalAccounts.map(ext => (
            <Card key={ext._id} className="hover:shadow-md transition-shadow">
              <CardContent className="pt-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center">
                      <Building2 className="h-5 w-5 text-[#0B5CAB]" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold">{ext.nickname || ext.bank_name}</p>
                      <p className="text-xs text-muted-foreground">{ext.bank_name}</p>
                    </div>
                  </div>
                  <Badge variant="secondary" className="text-xs">
                    <CheckCircle2 className="h-3 w-3 mr-1" />
                    {ext.status || 'Verified'}
                  </Badge>
                </div>

                <div className="space-y-2 mb-4">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Account</span>
                    <span className="font-mono">{ext.account_number_masked}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Routing</span>
                    <span className="font-mono">{ext.routing_number}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Type</span>
                    <span className="capitalize">{ext.account_type}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Holder</span>
                    <span>{ext.account_holder_name}</span>
                  </div>
                </div>

                <Separator className="my-3" />

                <div className="flex gap-2">
                  <Button
                    size="sm"
                    className="flex-1 text-xs"
                    onClick={() => openTransferDialog(ext)}
                    data-testid="ext-transfer-button"
                  >
                    <Send className="h-3 w-3 mr-1" /> Transfer
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="text-xs text-red-600 hover:text-red-700 hover:bg-red-50"
                    onClick={() => handleRemoveAccount(ext._id)}
                    data-testid="ext-remove-button"
                  >
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Transfer to External Dialog */}
      <Dialog open={transferDialogOpen} onOpenChange={setTransferDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Space Grotesk' }}>
              Transfer to {selectedExtAccount?.nickname || selectedExtAccount?.bank_name}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleExternalTransfer} className="space-y-4">
            <div className="p-3 rounded-lg bg-blue-50">
              <p className="text-xs text-blue-600 font-medium">Destination</p>
              <p className="text-sm font-semibold">{selectedExtAccount?.bank_name}</p>
              <p className="text-xs text-muted-foreground">{selectedExtAccount?.account_number_masked} &middot; {selectedExtAccount?.account_holder_name}</p>
            </div>
            <div className="space-y-2">
              <Label>From Account</Label>
              <Select value={fromAccountId} onValueChange={setFromAccountId}>
                <SelectTrigger><SelectValue placeholder="Select account" /></SelectTrigger>
                <SelectContent>
                  {accounts.map(a => (
                    <SelectItem key={a._id} value={a._id}>{a.name} ({formatCurrency(a.available_balance)})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {selectedFromAccount && <p className="text-xs text-muted-foreground">Available: {formatCurrency(selectedFromAccount.available_balance)}</p>}
            </div>
            <div className="space-y-2">
              <Label>Amount</Label>
              <DollarInput data-testid="ext-transfer-amount-input" value={transferAmount} onChange={e => setTransferAmount(e.target.value)} required />
            </div>
            <div className="space-y-2">
              <Label>Memo (Optional)</Label>
              <Textarea placeholder="Add a note" value={transferMemo} onChange={e => setTransferMemo(e.target.value)} />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setTransferDialogOpen(false)}>Cancel</Button>
              <Button data-testid="ext-transfer-submit-button" type="submit" disabled={transferLoading}>
                {transferLoading ? 'Processing...' : 'Review Transfer'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <PreTransferConfirm open={preConfirmOpen} onClose={setPreConfirmOpen} onConfirm={executeExternalTransfer} details={preConfirmDetails} loading={transferLoading} />
      <TransferConfirmation open={confirmOpen} onClose={setConfirmOpen} transferDetails={confirmDetails} />
    </div>
  );
}